import { Flex, Typography, Button, Form, Select, Input, Row, Col, Switch, DatePicker, message, Upload, Avatar, Modal, Image } from "antd"
import { ArrowLeftOutlined, UserOutlined, UploadOutlined, HomeOutlined, CheckSquareFilled, CloseOutlined } from "@ant-design/icons"
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js"
import "../../style/user.css"
import TextArea from "antd/es/input/TextArea";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { updateUser, uploadProfilePicture, deleteProfilePicture, connectOutlook, disconnectOutlook, getOutlookLoginDetails } from "../../services/adminServices";
import { showLoader } from "../../utils/showloader";
import dayjs from "dayjs";
import { formatDate, toDatePickerValue } from "../../utils/safeDate.js";
import { useDispatch } from "react-redux";
import { fetchUserInfo, updateProfilePicture, clearProfilePicture, fetchProfilePicture } from "../../redux/actions/userAction";
import BreadCrumb from "../BreadCrumb";
import UserRoleDropdown from "../UserRoleDropdown";
import TIME_ZONES from "../../consts/timeZones";
import { useTranslation } from "react-i18next";
import { FORM_LANGUAGE_OPTIONS } from "../../utils/formTranslations.js";

// (Removed avatar generator + styles)

const GeneralSettings = () => {
    const navigate = useTenantNavigation();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const formValues = Form.useWatch([], form);
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);
    // company logo removed for now
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [removeProfilePic, setRemoveProfilePic] = useState(false);
    const [, setUploading] = useState(false)
    const [isOutlookConnected, setIsOutlookConnected] = useState(false);

    const { userInfo, profilePicture: reduxProfilePicture } = useSelector(state => state.user)

    useEffect(() => {
        if (userInfo.userId) {

            let firstName = userInfo.name;
            let lastName = userInfo.lastName;

            if (userInfo.name) {
                if (!lastName) {
                    const nameParts = userInfo.name.trim().split(/\s+/);
                    if (nameParts.length > 0) {
                        firstName = nameParts[0];
                        if (nameParts.length > 1) {
                            lastName = nameParts.slice(1).join(" ");
                        }
                    }
                } else {
                    if (firstName && lastName && firstName.endsWith(lastName)) {
                        firstName = firstName.substring(0, firstName.length - lastName.length).trim();
                    }
                }
            }

            form.setFieldsValue({
                userId: userInfo.userId,
                name: firstName,
                lastName: lastName,
                email: userInfo.email,
                defaultLanguage: userInfo.defaultLanguage || "english",
                displayName: userInfo.displayName || firstName || "",
                defaultTimeZone: userInfo.defaultTimeZone || "ist",
                enableDelegation: userInfo.enableDelegation || false,
                delegatedUser: userInfo.delegatedUser || "",
                delegationStartDate: toDatePickerValue(userInfo.delegationStartDate) || "",
                delegationDueDate: toDatePickerValue(userInfo.delegationDueDate) || "",
                delegationDescription: userInfo.delegationDescription || ""
            })
            // company logo disabled
        }
    }, [userInfo, form])

    // Ensure profile picture is in Redux cache (header may have already loaded it)
    useEffect(() => {
        if (userInfo?.userId && userInfo?.profile_picture && !reduxProfilePicture) {
            dispatch(fetchProfilePicture(userInfo.userId, userInfo.profile_picture));
        }
    }, [userInfo?.userId, userInfo?.profile_picture, reduxProfilePicture, dispatch]);

    const parseOutlookConnectionStatus = (data) => {
        if (typeof data === "boolean") {
            return data;
        }

        if (!data || typeof data !== "object") {
            return false;
        }

        if (typeof data.connected === "boolean") return data.connected;
        if (typeof data.isConnected === "boolean") return data.isConnected;
        if (typeof data.outlookConnected === "boolean") return data.outlookConnected;
        if (typeof data.status === "string") return data.status.toLowerCase() === "connected";
        if (typeof data.loginStatus === "string") return data.loginStatus.toLowerCase() === "connected";

        return false;
    };

    const checkOutlookConnection = async () => {
        const response = await getOutlookLoginDetails();
        if (response.status === 200) {
            const connected = parseOutlookConnectionStatus(response.data);
            setIsOutlookConnected(connected);
            return;
        }

        setIsOutlookConnected(false);
    }

    useEffect(() => {
        if (userInfo?.userId) {
            checkOutlookConnection();
        }
    }, [userInfo?.userId])

    const goBack = () => {
        navigate(-1);
    }

    const handleSave = () => {
        form.submit();
    }

    const handleConnectOutlook = async () => {
        const tenantId = localStorage.getItem("tenantId") || "";
        const profileId = userInfo?.userId || localStorage.getItem("userId") || localStorage.getItem("id") || "";

        showLoader(true);
        const response = await connectOutlook(tenantId, profileId);
        showLoader(false);
        if (response.status !== 200) {
            message.error(response?.message || "Failed to initialize Outlook login.");
            return;
        }

        await checkOutlookConnection();
    }

    const handleDisconnectOutlook = async () => {
        showLoader(true);
        const response = await disconnectOutlook();
        showLoader(false);
        if (response.status === 200) {
            await checkOutlookConnection();
            message.success(t('Outlook disconnected successfully.'));
        } else {
            message.error(response?.message || "Failed to disconnect Outlook.");
        }
    }

    // Remove current profile picture preview
    const handleRemovePicture = async () => {
        // If a server-side picture exists, call DELETE API
        try {
            if (userInfo?.profile_picture) {
                showLoader(true);
                const resp = await deleteProfilePicture(userInfo.userId, userInfo.profile_picture);
                showLoader(false);
                if (resp.status === 200) {
                    message.success(t('Profile picture removed'));
                } else {
                    message.warning(t('Could not remove profile picture on server, clearing locally'));
                }
            }
        } catch {
            showLoader(false);
        }
        // Clear local state and mark for user update
        setProfilePicture(null);
        setProfilePictureUrl(null);
        setRemoveProfilePic(true);
        dispatch(clearProfilePicture());
    }



    const handleProfilePictureChange = async (info) => {
        const { file } = info;

        if (file.status === 'done' || file.originFileObj) {
            const fileObj = file.originFileObj || file;

            // Check file size (2MB limit)
            if (fileObj.size > 2 * 1024 * 1024) {
                message.error(t('File size must be less than 2MB'));
                return;
            }

            // Check file type
            const isValidType = ['image/jpeg', 'image/jpg', 'image/png'].includes(fileObj.type);
            if (!isValidType) {
                message.error(t('You can only upload JPG, JPEG or PNG files!'));
                return;
            }

            setProfilePicture(fileObj);
            setRemoveProfilePic(false);

            // Create preview URL
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfilePictureUrl(e.target.result);
            };
            reader.readAsDataURL(fileObj);
        }
    };

    // company logo handlers removed

    // removed unused upload prop objects

    const saveUser = async (values) => {

        const firstName = form.getFieldValue('name');
        const lastName = values.lastName;
        const fullName = lastName ? `${firstName} ${lastName}` : firstName;

        let postData = { userId: userInfo.userId, name: fullName, lastName: lastName, email: userInfo.email, displayName: values.displayName, defaultLanguage: values.defaultLanguage, defaultTimeZone: values.defaultTimeZone }

        // Delegation settings are part of the same form and saved together
        postData.enableDelegation = values.enableDelegation || false
        postData.delegationStartDate = ""
        postData.delegationDueDate = ""
        postData.delegationDescription = ""
        postData.delegatedUser = ""
        if (postData.enableDelegation) {
            postData.delegationStartDate = formatDate(values.delegationStartDate)
            postData.delegationDueDate = formatDate(values.delegationDueDate)
            postData.delegationDescription = values.delegationDescription
            postData.delegatedUser = values.delegatedUser
        }

        // Add profile picture to postData if changed or removed
        if (profilePicture) {
            postData.profile_picture = profilePicture?.name;
        }
        if (removeProfilePic && !profilePicture) {
            postData.profile_picture = ""; // signal removal
        }
        // company logo removed from save for now

        // return
        showLoader(true)
        const updateResponse = await updateUser({ ...postData, userId: userInfo.userId });
        showLoader(false)
        if (updateResponse.status === 200) {
            message.success(updateResponse.message)
            dispatch(fetchUserInfo())
        }
    }

    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
        if (!isJpgOrPng) {
            message.error(t('You can only upload JPG/PNG file'));
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error(t('Image must be smaller than 2MB'));
        }
        return isJpgOrPng && isLt2M;
    };

    const getBase64 = (img, callback) => {
        const reader = new FileReader();
        reader.addEventListener("load", () => callback(reader.result));
        reader.readAsDataURL(img);
    };

    const customRequest = async (options) => {
        const { file, onSuccess, onProgress } = options;
        setUploading(true);

        onProgress({ percent: 10 });
        const response = await uploadProfilePicture(file, userInfo?.userId);

        onProgress({ percent: 100 });
        if (response.status === 200) {
            onSuccess(response.data, file);
            message.success(t('Profile picture uploaded successfully'));
            getBase64(options.file, (url) => {
                setProfilePictureUrl(url);
                dispatch(updateProfilePicture(url));
            });
            // Don't manually update the file list here, let handleChange handle it
            // This prevents duplicates
        } else {
            message.error(t('Upload failed'))
        }



    };

    return (
        <div className="user-settings">
            <BreadCrumb items={[{ title: <HomeOutlined />, path: userInfo?.isAdmin ? "/app/dashboard" : "/app/dashboard" }, { title: t("Account Settings") }]} marginb="12px" />

            <Flex align="center" justify="space-between" style={{ paddingBottom: "12px" }}>
                <Typography.Title level={4} style={{ marginTop: 0 }}>
                    {t("Account Settings")}
                </Typography.Title>
                <Flex gap={8}>
                    <Button className="success-button" onClick={handleSave}>{t("Save")}</Button>
                </Flex>
            </Flex>
            <Form form={form} onFinish={saveUser} layout="vertical">
                <Flex gap={16} vertical>
                    <div className="general-settings settings-block">
                        <div className="font-semibold" style={{ margin: "0 0 10px 0", padding: 0, fontSize: "16px" }}>{t("General")}</div>

                        {/* Profile Picture Section */}
                        {/* profilePictureUrl = temporary local preview; reduxProfilePicture = persisted value */}
                        {(() => {
                            const displayPicture = profilePictureUrl || reduxProfilePicture;
                            return (
                        <Row style={{ marginBottom: '24px' }}>
                            <Col span={24}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <Avatar
                                        size={64}
                                        src={displayPicture}
                                        icon={!displayPicture && <UserOutlined />}
                                        style={{
                                            backgroundColor: !displayPicture ? '#f56a00' : undefined,
                                            flexShrink: 0
                                        }}
                                        shape="square"
                                        className="cursor-pointer ring-1 ring-gray-200 hover:ring-indigo-400"
                                        onClick={() => displayPicture && setIsPreviewOpen(true)}
                                    />
                                    <div>
                                        <Typography.Text style={{ display: 'block', marginBottom: '4px' }}>
                                            {t("Profile Picture")}
                                        </Typography.Text>
                                        <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                            {t("You can use JPG, JPEG or PNG files. Max size: 2MB")}
                                        </Typography.Text>
                                        <Flex gap={8}>
                                            <Upload
                                                name="file"
                                                accept=".jpg,.jpeg,.png"
                                                showUploadList={false}
                                                beforeUpload={beforeUpload}
                                                customRequest={customRequest}
                                                onChange={handleProfilePictureChange}
                                            >
                                                <Button size="small" icon={<UploadOutlined />} >
                                                    <span style={{ fontSize: "12px" }}>{t("Choose Image")}</span>
                                                </Button>
                                            </Upload>
                                            {
                                                displayPicture &&
                                                <Button size="small" type="text" danger onClick={handleRemovePicture} disabled={!displayPicture} className="!px-2">
                                                    <span style={{ fontSize: "12px" }}>{t("Remove")}</span>
                                                </Button>
                                            }

                                        </Flex>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                            );
                        })()}

                        {/* Company Logo Section */}
                        {/* <Row style={{ marginBottom: '24px' }}>
                            <Col span={24}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <Avatar 
                                        size={64} 
                                        src={companyLogoUrl} 
                                        icon={!companyLogoUrl && <UserOutlined />}
                                        style={{ 
                                            backgroundColor: !companyLogoUrl ? '#1890ff' : undefined,
                                            flexShrink: 0 
                                        }}
                                        shape="square"
                                    />
                                    <div>
                                        <Typography.Text strong style={{ display: 'block', marginBottom: '4px' }}>
                                            Company Logo
                                        </Typography.Text>
                                        <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                            You can use JPG, GIF or PNG files. Max size: 2MB
                                        </Typography.Text>
                                        <Upload {...logoUploadProps}>
                                            <Button size="small" icon={<UploadOutlined />}>
                                                Choose File
                                            </Button>
                                        </Upload>
                                    </div>
                                </div>
                            </Col>
                        </Row> */}

                        <Row>
                            <Col span={9}>
                                <Form.Item label={t("First Name")} name="name">
                                    <Input placeholder={t("Enter first name")} disabled />
                                </Form.Item>
                            </Col>
                            <Col span={1}></Col>
                            <Col span={9}>
                                <Form.Item label={t("Last Name")} name="lastName">
                                    <Input placeholder={t("Enter last name")} disabled />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={9}>
                                <Form.Item label={t("Display Name")} name="displayName">
                                    <Input placeholder={t("Enter display name")} />
                                </Form.Item>
                            </Col>
                            <Col span={1}></Col>
                            <Col span={9}>
                                <Form.Item label={t("Email Address")} name="email">
                                    <Input placeholder={t("Enter email")} disabled />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={9}>
                                <Form.Item label={t("Language")} name="defaultLanguage">
                                    <Select options={FORM_LANGUAGE_OPTIONS} />
                                </Form.Item>
                            </Col>
                            <Col span={1}></Col>
                            <Col span={9}>
                                <Form.Item label={t("Time Zone")} name="defaultTimeZone">
                                    <Select
                                        options={TIME_ZONES}
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder={t("Select time zone")}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div className="delegation-settings settings-block">
                        <div className="font-semibold" style={{ margin: "0 0 10px 0", padding: 0, fontSize: "16px" }}>{t("Delegation Setting")}</div>
                        <Row>
                            <Col span={12}>
                                <Form.Item label={t("Enable delegation")} name="enableDelegation" className="horizontal-form-item">
                                    <Switch size="small" onChange={(checked) => {
                                        if (!checked) {
                                            form.setFieldsValue({
                                                delegatedUser: "",
                                                delegationStartDate: null,
                                                delegationDueDate: null,
                                                delegationDescription: ""
                                            })
                                        }
                                    }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        {
                            formValues?.enableDelegation ?
                                <>
                                    <Row>
                                        <Col span={11}>
                                            <Form.Item label={t("User to delegate")} name="delegatedUser" rules={[{ required: true, message: t("User to delegate is required") }]}>
                                                <UserRoleDropdown
                                                    placeholder={t("Select user")}
                                                    tabsRequired={['user']}
                                                    mode="SINGLE"
                                                    selectedOptions={{ user: formValues?.delegatedUser ? [formValues.delegatedUser] : [], group: [], role: [] }}
                                                    onSelect={(value) => {
                                                        const selectedEmail = value?.user?.[0] || ""
                                                        form.setFieldValue("delegatedUser", selectedEmail)
                                                        if (form.validateFields) {
                                                            form.validateFields(["delegatedUser"])
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col span={5}>
                                            <Form.Item label={t("Start Date")} name="delegationStartDate" rules={[{ required: true, message: t("Start date is required") }]}>
                                                <DatePicker format={'YYYY-MM-DD'} placeholder={t("Select start date")} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={1}></Col>
                                        <Col span={5}>
                                            <Form.Item label={t("End Date")} name="delegationDueDate" rules={[{ required: true, message: t("End date is required") }]}>
                                                <DatePicker format={'YYYY-MM-DD'} placeholder={t("Select end date")} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col span={11}>
                                            <Form.Item label={t("Description")} name="delegationDescription">
                                                <TextArea placeholder={t("Enter description for delegation (optional)")} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </>
                                : null
                        }
                    </div>

                    <div className="integrations-settings settings-block">
                        <div className="font-semibold" style={{ margin: "0 0 16px 0", padding: 0, fontSize: "16px" }}>{t("Integrations")}</div>

                        <div style={{ display: "flex", alignItems: "center", marginBottom: "12px", gap: "8px" }}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24px" height="24px">
                                <path fill="#0078d4" d="M37,13l-10-3v28l10-3V13z M27,34V14l8,2.4v15.2L27,34z" />
                                <path fill="#28a8ea" d="M11,16v16l16,4V12L11,16z M24,33l-11-2.7V17.7L24,15V33z" />
                                <path fill="#fff" d="M19.1,23.3c0-0.6-0.1-1.2-0.4-1.6c-0.2-0.5-0.6-0.8-1-1.1c-0.5-0.2-1-0.3-1.6-0.3c-0.7,0-1.2,0.1-1.7,0.4 c-0.5,0.3-0.8,0.7-1,1.2C13.2,22.4,13,23.1,13,24c0,0.9,0.1,1.6,0.4,2.1c0.3,0.5,0.6,0.9,1.1,1.1c0.5,0.2,1,0.4,1.6,0.4 c0.7,0,1.2-0.1,1.7-0.4c0.5-0.3,0.8-0.7,1-1.2C19,25.6,19.1,24.9,19.1,23.3z M17.6,24.1c0,0.5-0.1,1-0.2,1.3 c-0.1,0.3-0.3,0.6-0.5,0.7c-0.2,0.1-0.5,0.2-0.8,0.2c-0.3,0-0.6-0.1-0.8-0.2c-0.2-0.2-0.4-0.4-0.5-0.7c-0.1-0.3-0.2-0.8-0.2-1.3 c0-0.5,0.1-1,0.2-1.3c0.1-0.3,0.3-0.6,0.5-0.7c0.2-0.2,0.5-0.2,0.8-0.2c0.3,0,0.6,0.1,0.8,0.2c0.2,0.2,0.4,0.4,0.5,0.7 C17.5,23.1,17.6,23.5,17.6,24.1z M22,27.5h-1.3v-7.1h1.3V27.5z" />
                            </svg>
                            <span style={{ fontSize: "16px", fontWeight: 500 }}>{t('Microsoft Outlook')}</span>
                        </div>

                        {isOutlookConnected ? (
                            <>
                                <div style={{ display: "flex", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
                                    <span style={{ fontWeight: 500 }}>{t("Status")}:</span>
                                    <span style={{ color: "#52c41a", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <CheckSquareFilled style={{ fontSize: "16px" }} /> {t("Connected")}
                                    </span>
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                    <span style={{ fontWeight: 500 }}>{t("Connected by")}:</span> {userInfo?.email || t("Unknown")}
                                </div>
                                <Flex gap={8}>
                                    <Button onClick={handleDisconnectOutlook}>{t("Disconnect")}</Button>
                                </Flex>
                            </>
                        ) : (
                            <>
                                <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", gap: "8px" }}>
                                    <span style={{ fontWeight: 500 }}>{t("Status")}:</span>
                                    <span style={{ color: "#ff4d4f", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <CloseOutlined style={{ fontSize: "16px", backgroundColor: "#ffeaeb", padding: "2px", borderRadius: "4px" }} /> {t("Not Connected")}
                                    </span>
                                </div>
                                <Button type="primary" onClick={handleConnectOutlook}>{t("Connect Outlook Account")}</Button>
                            </>
                        )}
                    </div>
                </Flex>
            </Form>

            {/* Image Preview Modal */}
            <Modal
                title={t("Profile picture")}
                open={isPreviewOpen}
                footer={null}
                onCancel={() => setIsPreviewOpen(false)}
                width={420}
            >
                {(() => {
                    const displayPicture = profilePictureUrl || reduxProfilePicture;
                    return displayPicture ? (
                        <Image src={displayPicture} width={360} />
                    ) : (
                        <Typography.Text type="secondary">{t("No image")}</Typography.Text>
                    );
                })()}
            </Modal>
        </div>
    )
}

export default GeneralSettings
